import http from "@/services/http";

export interface Dealer {
  id: string;
  name: string;
  description?: string;
  address?: string;
  phone?: string;
  email?: string;
  title?: string;
  logoUrl?: string;
  primaryColor?: string;
  secondaryColor?: string;
  customDomain?: string;
  customDomainEnabled?: boolean;
  active?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface UpdateDealerBrandingRequest {
  title: string;
  description: string;
  primaryColor: string;
  secondaryColor: string;
}

export interface UpdateDealerDomainRequest {
  customDomain: string;
  customDomainEnabled: boolean;
}

export interface DealerListParams {
  search?: string | null;
  isActive?: boolean | null;
  page?: number;
  pageSize?: number;
}

export interface DealerCreateRequest {
  name: string;
  description?: string;
  address?: string;
  phone?: string;
  email?: string;
  active?: boolean;
}

export interface DealerUpdateRequest {
  name?: string;
  description?: string;
  address?: string;
  phone?: string;
  email?: string;
  active?: boolean;
}

export interface DealerListResponse {
  items: Dealer[];
  total: number;
}

export interface DealerDashboardPayment {
  loanId: string;
  customerUserId: string;
  customerName?: string;
  monthNumber: number;
  dueDate: string;
  plannedPayment: number;
}

export interface DealerDashboard {
  totalCustomers: number;
  activeLoans: number;
  upcomingPaymentsCount: number;
  fromDate?: string;
  toDate?: string;
  upcomingPayments?: DealerDashboardPayment[];
}

class DealerService {
  async listDealers(params: DealerListParams = {}): Promise<DealerListResponse> {
    const query = new URLSearchParams();
    if (params.search) query.set("search", params.search);
    if (params.isActive !== null && params.isActive !== undefined) {
      query.set("isActive", String(params.isActive));
    }
    if (params.page) query.set("page", String(params.page));
    if (params.pageSize) query.set("pageSize", String(params.pageSize));
    const qs = query.toString();

    const response = await http.get<
      Dealer[] | { items?: Dealer[]; total?: number; totalItems?: number }
    >(
      `/dealers${qs ? `?${qs}` : ""}`
    );

    if (Array.isArray(response)) {
      return { items: response, total: response.length };
    }

    const items = Array.isArray(response?.items) ? response.items : [];
    const totalFromResponse =
      typeof response?.total === "number"
        ? response.total
        : typeof response?.totalItems === "number"
          ? response.totalItems
          : undefined;
    const total = totalFromResponse ?? items.length;
    return { items, total };
  }

  async getDealerById(dealerId: string): Promise<Dealer> {
    return http.get<Dealer>(`/dealers/${dealerId}`);
  }

  async createDealer(data: DealerCreateRequest): Promise<Dealer> {
    return http.post<Dealer>("/dealers", data);
  }

  async updateDealer(dealerId: string, data: DealerUpdateRequest): Promise<Dealer> {
    return http.put<Dealer>(`/dealers/${dealerId}`, data);
  }

  async deleteDealer(dealerId: string): Promise<void> {
    await http.delete<void>(`/dealers/${dealerId}`);
  }

  // GET /api/dealers/me — any Dealer user gets their own dealer config
  async getMyDealer(): Promise<Dealer> {
    return http.get<Dealer>("/dealers/me");
  }

  // Admin: PUT /api/dealers/{id}/branding
  async updateDealerBranding(dealerId: string, data: UpdateDealerBrandingRequest): Promise<Dealer> {
    return http.put<Dealer>(`/dealers/${dealerId}/branding`, data);
  }

  // DealerAdmin: PUT /api/dealers/me/branding
  async updateMyBranding(data: UpdateDealerBrandingRequest): Promise<Dealer> {
    return http.put<Dealer>("/dealers/me/branding", data);
  }

  // Admin: POST /api/dealers/{id}/logo
  async uploadDealerLogo(dealerId: string, file: File): Promise<Dealer> {
    const formData = new FormData();
    formData.append("file", file);
    return http.postFormData<Dealer>(`/dealers/${dealerId}/logo`, formData);
  }

  // DealerAdmin: POST /api/dealers/me/logo
  async uploadMyLogo(file: File): Promise<Dealer> {
    const formData = new FormData();
    formData.append("file", file);
    return http.postFormData<Dealer>("/dealers/me/logo", formData);
  }

  // Admin: DELETE /api/dealers/{id}/logo
  async deleteDealerLogo(dealerId: string): Promise<Dealer> {
    return http.delete<Dealer>(`/dealers/${dealerId}/logo`);
  }

  // DealerAdmin: DELETE /api/dealers/me/logo
  async deleteMyLogo(): Promise<Dealer> {
    return http.delete<Dealer>("/dealers/me/logo");
  }

  // Admin only: PUT /api/dealers/{id}/domain
  async updateDomain(dealerId: string, data: UpdateDealerDomainRequest): Promise<Dealer> {
    return http.put<Dealer>(`/dealers/${dealerId}/domain`, data);
  }

  async getDashboard(params: { dealerId?: string; daysAhead?: number } = {}): Promise<DealerDashboard> {
    const query = new URLSearchParams();
    if (params.dealerId) query.set("dealerId", params.dealerId);
    if (params.daysAhead) query.set("daysAhead", String(params.daysAhead));
    const qs = query.toString();
    return http.get<DealerDashboard>(`/dealers/dashboard${qs ? `?${qs}` : ""}`);
  }
}

export default new DealerService();
